import { getHubSpotClient } from "@/lib/hubspot/client";
import { z } from "zod";

const MAX_BATCH_READ_ATTEMPTS = 3;
const RETRY_BASE_DELAYS_MS = [300, 900];
const TRANSIENT_STATUS_CODES = new Set([423, 429, 502, 503, 504, 521, 522, 523, 524]);
const ERROR_BODY_SNIPPET_MAX_LENGTH = 500;

const ALLOWED_ASSOCIATION_PAIRS = new Set([
  "companies->contacts",
  "companies->deals",
  "deals->companies",
  "deals->line_items",
  "leads->companies",
  "leads->contacts",
  "leads->deals",
]);

class HubSpotAssociationReadError extends Error {
  constructor(
    message: string,
    public readonly details: {
      operation: string;
      status: number;
      attemptCount: number;
      bodySnippet?: string;
    }
  ) {
    super(message);
    this.name = "HubSpotAssociationReadError";
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function sanitizeErrorBody(text: string): string | undefined {
  const clean = text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, " ")
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!clean) {
    return undefined;
  }

  return clean.length > ERROR_BODY_SNIPPET_MAX_LENGTH
    ? `${clean.slice(0, ERROR_BODY_SNIPPET_MAX_LENGTH - 3)}...`
    : clean;
}

function getRetryAfterMs(response: Response): number | undefined {
  const retryAfter = response.headers.get("retry-after");
  if (!retryAfter) {
    return undefined;
  }

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }

  const retryAt = Date.parse(retryAfter);
  if (Number.isNaN(retryAt)) {
    return undefined;
  }

  return Math.max(0, retryAt - Date.now());
}

function getRetryDelayMs(response: Response, attemptIndex: number): number {
  const retryAfterMs = getRetryAfterMs(response);
  if (retryAfterMs !== undefined) {
    return Math.min(retryAfterMs, 5_000);
  }

  const baseDelay = RETRY_BASE_DELAYS_MS[attemptIndex - 1] ?? 900;
  return baseDelay + Math.floor(Math.random() * 100);
}

function getOperation(fromObjectType: string, toObjectType: string): string {
  return `${fromObjectType}->${toObjectType}.batchRead`;
}

const associationIdSchema = z.union([z.string(), z.number()]).transform(String);
const associationFromSchema = z.union([
  z.object({ id: associationIdSchema }).transform(({ id }) => id),
  z.object({ objectId: associationIdSchema }).transform(({ objectId }) => objectId),
  z.string(),
  z.number().transform(String),
]);
const associationToSchema = z.union([
  z.object({ id: associationIdSchema }).transform(({ id }) => id),
  z.object({ toObjectId: associationIdSchema }).transform(({ toObjectId }) => toObjectId),
]);
const batchAssociationResponseSchema = z.object({
  results: z.array(
    z
      .object({
        from: associationFromSchema.optional(),
        fromObjectId: associationIdSchema.optional(),
        to: z.array(associationToSchema).optional(),
      })
      .transform(({ from, fromObjectId, to }) => ({
        fromId: from ?? fromObjectId,
        toIds: to ?? [],
      })),
  ),
});

function parseBatchAssociationResponse(
  payload: z.input<typeof batchAssociationResponseSchema>,
  operation: string,
  associationPair: string,
): z.output<typeof batchAssociationResponseSchema> {
  try {
    return batchAssociationResponseSchema.parse(payload);
  } catch {
    throw new HubSpotAssociationReadError(
      `Invalid batch association response ${associationPair}`,
      { operation, status: 502, attemptCount: MAX_BATCH_READ_ATTEMPTS },
    );
  }
}

export async function batchReadAssociations(
  fromObjectType: string,
  toObjectType: string,
  fromIds: Array<string | number>
): Promise<Map<string, string[]>> {
  const uniqueIds = Array.from(new Set(
    fromIds
      .filter(Boolean)
      .map((id) => String(id))
  ));

  const resultsMap = new Map<string, string[]>();
  uniqueIds.forEach((id) => resultsMap.set(id, []));

  if (uniqueIds.length === 0) return resultsMap;

  const associationPair = `${fromObjectType}->${toObjectType}`;
  if (!ALLOWED_ASSOCIATION_PAIRS.has(associationPair)) {
    throw new HubSpotAssociationReadError(
      `Association pair ${associationPair} is not allowlisted`,
      { operation: getOperation(fromObjectType, toObjectType), status: 403, attemptCount: 0 },
    );
  }

  for (let i = 0; i < uniqueIds.length; i += 1000) {
    const chunk = uniqueIds.slice(i, i + 1000);
    const operation = getOperation(fromObjectType, toObjectType);
    let response: Response | null = null;
    let bodySnippet: string | undefined;

    for (let attempt = 1; attempt <= MAX_BATCH_READ_ATTEMPTS; attempt += 1) {
      response = await getHubSpotClient().apiRequest({
        method: "POST",
        path: `/crm/v4/associations/${fromObjectType}/${toObjectType}/batch/read`,
        body: { inputs: chunk.map((id) => ({ id })) },
      });

      if (response.ok) {
        break;
      }

      bodySnippet = sanitizeErrorBody(await response.text());

      if (!TRANSIENT_STATUS_CODES.has(response.status) || attempt === MAX_BATCH_READ_ATTEMPTS) {
        throw new HubSpotAssociationReadError(
          `Failed to batch read associations ${fromObjectType}->${toObjectType}: HTTP ${response.status}`,
          {
            operation,
            status: response.status,
            attemptCount: attempt,
            bodySnippet,
          }
        );
      }

      await sleep(getRetryDelayMs(response, attempt));
    }

    if (!response?.ok) {
      throw new HubSpotAssociationReadError(
        `Failed to batch read associations ${fromObjectType}->${toObjectType}`,
        {
          operation,
          status: response?.status ?? 0,
          attemptCount: MAX_BATCH_READ_ATTEMPTS,
          bodySnippet,
        }
      );
    }

    const data = parseBatchAssociationResponse(await response.json(), operation, associationPair);

    data.results.forEach((item) => {
      const fromId = item.fromId;
      if (!fromId) return;

      const existing = resultsMap.get(fromId) ?? [];
      const merged = new Set(existing);
      item.toIds.forEach((id) => merged.add(id));
      resultsMap.set(fromId, Array.from(merged));
    });
  }

  return resultsMap;
}
